import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {
  defaultOptions,
  FeatureFlagService,
  HttpOptionsBuilder,
  pdfOptions,
  responseToObject,
} from '@emma-labs/shared/angular';
import {
  DocumentName,
  ENVIRONMENT,
  EnvironmentBroker,
  ExtraRequirementsInquiry,
  HealthAnswersRevision,
  MinifiedUnderwritingProfile,
  ModificationNoticeBreakdown,
  TextTranslations,
  UnderwritingProfile,
} from '@emma-labs/shared/typescript';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {handleHttpError} from '../../utils/utility-constants';
import {AuthService} from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class UnderwritingProfileApiService {
  private readonly basePath: string;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly featureFlagService: FeatureFlagService,
    @Inject(ENVIRONMENT) readonly environment: EnvironmentBroker,
  ) {
    this.basePath = `${environment.msSaleApiBase}/underwriting-profiles`;
  }

  getAll(sessionId: string): Observable<MinifiedUnderwritingProfile[]> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(this.basePath);

    return this.http.get(url.toString(), options).pipe(
      map(
        (response) =>
          responseToObject<{
            underwritingProfiles: MinifiedUnderwritingProfile[];
          }>(response as HttpResponse<string>).underwritingProfiles,
      ),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getById(sessionId: string, underwritingProfileId: string): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}`);

    return this.http.get(url.toString(), options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  saveNote(sessionId: string, underwritingProfileId: string, text: string): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/notes`);

    return this.http.post(url.toString(), {text}, options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  updateSurchargeRate(
    sessionId: string,
    underwritingProfileId: string,
    surchargeRate: number | null,
  ): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/surcharge-rate`);

    return this.http.put(url.toString(), {surchargeRate}, options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  updateIsIncludedInContract(
    sessionId: string,
    underwritingProfileId: string,
    isIncludedInContract: boolean,
    healthAnswersRevisionId: string,
  ): Observable<undefined> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/is-included-in-contract`);
    return this.http
      .post(
        url.toString(),
        {
          isIncludedInContract: isIncludedInContract,
          healthAnswersRevisionId: healthAnswersRevisionId,
        },
        options,
      )
      .pipe(
        map(() => undefined),
        catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
      );
  }

  createHealthAnswersRevision(
    sessionId: string,
    underwritingProfileId: string,
    healthAnswersRevision: Omit<
      HealthAnswersRevision,
      'id' | 'createdAt' | 'isAutoGenerated' | 'isIncludedInContract'
    >,
  ): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/health-answers-revision`);

    return this.http.post(url.toString(), healthAnswersRevision, options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  submitExtraRequirements(
    sessionId: string,
    underwritingProfileId: string,
    extraRequirements: Omit<ExtraRequirementsInquiry, 'id' | 'createdAt'>,
  ): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/extra-requirements`);

    return this.http.post(url.toString(), extraRequirements, options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  refuse(
    sessionId: string,
    underwritingProfileId: string,
    refusalMessage: TextTranslations,
  ): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/refuse`);

    return this.http.put(url.toString(), {refusalMessage}, options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  approve(
    sessionId: string,
    underwritingProfileId: string,
    modificationNoticeBreakdown?: ModificationNoticeBreakdown,
  ): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/approve`);

    return this.http
      .put(
        url.toString(),
        {
          modificationNotice: modificationNoticeBreakdown?.modificationNoticeText,
          modificationConditionText: modificationNoticeBreakdown?.conditionText,
        },
        options,
      )
      .pipe(
        map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
        catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
      );
  }

  cancel(sessionId: string, underwritingProfileId: string): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/cancel`);

    return this.http.put(url.toString(), {}, options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  uploadExtraRequirementsDocuments(
    sessionId: string,
    underwritingProfileId: string,
    documents: File[],
  ): Observable<UnderwritingProfile> {
    const options = new HttpOptionsBuilder().withApiKey(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/documents`);
    const body = new FormData();
    documents.forEach((document) => body.append('files', document));

    return this.http.post(url.toString(), body, options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  removeExtraRequirementsDocument(
    sessionId: string,
    underwritingProfileId: string,
    documentName: string,
  ): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/documents/${documentName}`);

    return this.http.delete(url.toString(), options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getExtraRequirementsDocument(
    sessionId: string,
    underwritingProfileId: string,
    documentName: string,
  ): Observable<Blob> {
    const options = pdfOptions(sessionId).buildOptions();
    const url = new URL(
      `${this.basePath}/${btoa(underwritingProfileId)}}/documents/extra-requirements/${documentName}`,
    );

    return this.http.get(url.toString(), {...options, responseType: 'blob'}).pipe(
      map((res) => res.body as Blob),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getDocument(id: string, underwritingProfileId: string, document: DocumentName): Observable<Blob> {
    const options = pdfOptions(id).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/documents/${document}`);

    return this.http.get(url.toString(), {...options, responseType: 'blob'}).pipe(
      map((res) => res.body as Blob),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  saveUnderwriter(
    sessionId: string,
    underwritingProfileId: string,
    agentId?: string,
  ): Observable<UnderwritingProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(underwritingProfileId)}/underwriter`);

    return this.http.put(url.toString(), {agentId}, options).pipe(
      map((response) => responseToObject<UnderwritingProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }
}
